"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/shared/utils/cn";
import { RecoverPasswordSchema } from "../../auth.schema";
import { useRecoverPasswordMutation } from "../../auth.mutations";

interface ResetPasswordFormProps extends React.ComponentPropsWithoutRef<"div"> {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export function ResetPasswordForm({ className, onSuccess, onError, ...props }: ResetPasswordFormProps) {
  const router = useRouter();
  const recoverPasswordMutation = useRecoverPasswordMutation();

  const form = useForm<z.infer<typeof RecoverPasswordSchema>>({
    resolver: zodResolver(RecoverPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof RecoverPasswordSchema>) => {
    try {
      const response = await recoverPasswordMutation.mutateAsync(values);

      if (response.status === "success") {
        form.reset();
        onSuccess?.();
        router.push("/sign-in");
      } else {
        onError?.(response);
      }

    } catch (error) {
      onError?.(error);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="rounded-2xl bg-gray-30 dark:bg-black/10">
        <CardHeader>
          <CardTitle className="text-2xl">Resetar Senha</CardTitle>
          <CardDescription>Insira o e-mail associado à sua conta para redefinir sua senha</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="seuemail@exemplo.com"
                        disabled={recoverPasswordMutation.isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={recoverPasswordMutation.isPending}>
                {recoverPasswordMutation.isPending ? "Enviando..." : "Enviar link para redefinir senha"}
              </Button>

              <div className="text-center text-sm">
                Lembrou sua senha?{" "}
                <Link href="/sign-in" className="underline underline-offset-4">
                  Faça login
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
